import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { verifyOf } from "./agentic";
import { EmptyState, ItemRow } from "./item-ui";
import {
  AGENT_FILTERS,
  agentFor,
  isApproved,
  isPending,
  isPublished,
  isRejected,
  kindOf,
  toDate,
  type ItemKind,
  type StudioItem,
} from "./helpers";

type StatusFilter = "published" | "pulled_back" | "approved" | "waiting" | "rejected" | "all";
type TypeFilter = "all" | "article" | "social" | "email" | "ad" | "plan" | "brief";

const STATUS_FILTERS: { id: StatusFilter; label: string }[] = [
  { id: "published", label: "Published" },
  { id: "pulled_back", label: "Pulled back" },
  { id: "approved", label: "Approved" },
  { id: "waiting", label: "Waiting for you" },
  { id: "rejected", label: "Rejected" },
  { id: "all", label: "Everything" },
];

const TYPE_FILTERS: { id: TypeFilter; label: string }[] = [
  { id: "all", label: "All types" },
  { id: "article", label: "Articles" },
  { id: "social", label: "Social posts" },
  { id: "email", label: "Email campaigns" },
  { id: "ad", label: "Ads" },
  { id: "plan", label: "Plans" },
  { id: "brief", label: "Briefs" },
];

const PAGE = 40;

/** Published, then taken down again because the check after publishing found a problem. */
function isPulledBack(item: StudioItem): boolean {
  return verifyOf(item)?.state === "pulled_back";
}

function matchesStatus(item: StudioItem, f: StatusFilter): boolean {
  switch (f) {
    case "published": return isPublished(item) && !isRejected(item) && !isPulledBack(item);
    case "pulled_back": return isPulledBack(item) && !isRejected(item);
    case "approved": return isApproved(item) && !isPublished(item);
    case "waiting": return isPending(item);
    case "rejected": return isRejected(item);
    default: return true;
  }
}

const selectClass =
  "rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60";

/** Plain library: every item as a document row, with three filters. */
export function PublishedTab({
  items,
  loading,
  onOpen,
}: {
  items: StudioItem[];
  loading: boolean;
  onOpen: (item: StudioItem) => void;
}) {
  const [status, setStatus] = useState<StatusFilter>("published");
  const [type, setType] = useState<TypeFilter>("all");
  const [agent, setAgent] = useState<string>("all");
  const [limit, setLimit] = useState(PAGE);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = { published: 0, pulled_back: 0, approved: 0, waiting: 0, rejected: 0, all: items.length };
    for (const i of items) {
      (["published", "pulled_back", "approved", "waiting", "rejected"] as const).forEach((f) => {
        if (matchesStatus(i, f)) c[f]++;
      });
    }
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    return items
      .filter((i) => matchesStatus(i, status))
      .filter((i) => {
        if (type === "all") return true;
        const k: ItemKind = kindOf(i);
        return k === type;
      })
      .filter((i) => agent === "all" || agentFor(i).name === agent)
      .sort((a, b) => {
        const byPublish = status === "published" || status === "pulled_back";
        const ta = toDate(byPublish ? a.publishedAt || a.createdAt : a.createdAt)?.getTime() ?? 0;
        const tb = toDate(byPublish ? b.publishedAt || b.createdAt : b.createdAt)?.getTime() ?? 0;
        return tb - ta;
      });
  }, [items, status, type, agent]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="mr-2 animate-spin" size={18} /> Loading…
      </div>
    );
  }

  const reset = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setLimit(PAGE);
  };

  return (
    <div className="space-y-4" data-testid="published-tab">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Status" data-testid="filter-status">
        {STATUS_FILTERS.filter((f) => f.id !== "pulled_back" || counts.pulled_back > 0 || status === "pulled_back").map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => reset(setStatus)(f.id)}
            aria-pressed={status === f.id}
            data-testid={`filter-status-${f.id}`}
            className={`rounded-full border px-3 py-1 text-sm transition-colors ${
              status === f.id
                ? "border-blue-500/50 bg-blue-600/20 text-blue-200"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {f.label} <span className="text-xs opacity-80">{counts[f.id]}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <label className="sr-only" htmlFor="filter-type">Type</label>
        <select id="filter-type" className={selectClass} value={type} onChange={(e) => reset(setType)(e.target.value as TypeFilter)} data-testid="filter-type">
          {TYPE_FILTERS.map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
        <label className="sr-only" htmlFor="filter-agent">Written by</label>
        <select id="filter-agent" className={selectClass} value={agent} onChange={(e) => reset(setAgent)(e.target.value)} data-testid="filter-agent">
          <option value="all">All agents</option>
          {AGENT_FILTERS.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <span className="self-center text-xs text-muted-foreground" data-testid="library-count">
          {filtered.length} item{filtered.length === 1 ? "" : "s"}
        </span>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title={status === "published" ? "Nothing has been published from here yet." : status === "pulled_back" ? "Nothing has been pulled back." : "No items match these filters."}>
          {status !== "all" && (
            <Button type="button" variant="outline" size="sm" onClick={() => { setStatus("all"); setType("all"); setAgent("all"); setLimit(PAGE); }}>
              Show everything
            </Button>
          )}
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {filtered.slice(0, limit).map((item) => (
            <ItemRow key={item.id} item={item} onOpen={onOpen} />
          ))}
          {filtered.length > limit && (
            <Button type="button" variant="outline" size="sm" onClick={() => setLimit((l) => l + PAGE)} data-testid="button-library-more">
              Show {Math.min(PAGE, filtered.length - limit)} more
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
